import { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  Target,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Building2,
  Briefcase,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import DangerZoneButton from '../../components/admin/DangerZoneButton';
import SentinelCleanupPanel from '../../components/admin/SentinelCleanupPanel';
import DbMigrationPanel from '../../components/admin/DbMigrationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StrategicDashboard() {
  const { authToken } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchKPIs = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/strategic-kpis`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch KPIs');
      }
      
      const data = await response.json();
      setKpis(data);
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchKPIs();
    }
  }, [authToken]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(1)}%`;
  };

  return (
    <div data-testid="strategic-dashboard">
      <PageHeader 
        title="Strategic Insights"
        description="High-level KPIs to drive your 2026 growth strategy"
        action={
          <Button
            variant="outline"
            onClick={fetchKPIs}
            disabled={isLoading}
            className="border-slate-200"
            data-testid="refresh-kpis-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />
      
      <div className="p-8 space-y-8">
        {isLoading && !kpis ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : error && !kpis ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <p className="text-red-700">{error}</p>
              <Button 
                onClick={fetchKPIs} 
                variant="outline" 
                className="mt-4"
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Primary KPIs - Big Numbers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Monthly Recurring Revenue */}
              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-emerald-100 rounded-xl">
                      <DollarSign className="w-6 h-6 text-emerald-600" />
                    </div>
                    {kpis?.mrr_trend && (
                      <Badge className={`${kpis.mrr_trend >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {kpis.mrr_trend >= 0 ? (
                          <ArrowUpRight className="w-3 h-3 mr-1" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3 mr-1" />
                        )}
                        {kpis.mrr_trend >= 0 ? '+' : ''}{kpis.mrr_trend}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                    Monthly Recurring Revenue
                  </p>
                  <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatCurrency(kpis?.mrr || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    From {kpis?.active_subscriptions || 0} active subscriptions
                  </p>
                </CardContent>
              </Card>

              {/* Trial-to-Paid Conversion Rate */}
              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    {kpis?.conversion_trend && (
                      <Badge className={`${kpis.conversion_trend >= 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {kpis.conversion_trend >= 0 ? (
                          <ArrowUpRight className="w-3 h-3 mr-1" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3 mr-1" />
                        )}
                        {kpis.conversion_trend >= 0 ? '+' : ''}{kpis.conversion_trend}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                    Trial-to-Paid Conversion
                  </p>
                  <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatPercentage(kpis?.trial_to_paid_rate || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    {kpis?.conversions_this_month || 0} conversions this month
                  </p>
                </CardContent>
              </Card>

              {/* Total Pipeline Value */}
              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                    {kpis?.pipeline_trend && (
                      <Badge className={`${kpis.pipeline_trend >= 0 ? 'bg-purple-100 text-purple-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {kpis.pipeline_trend >= 0 ? (
                          <ArrowUpRight className="w-3 h-3 mr-1" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3 mr-1" />
                        )}
                        {kpis.pipeline_trend >= 0 ? '+' : ''}{kpis.pipeline_trend}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                    Total Pipeline Value
                  </p>
                  <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatCurrency(kpis?.total_pipeline_value || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Across {kpis?.active_pursuits_count || 0} active pursuits
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Active Contractors</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{kpis?.active_clients || 0}</p>
                    </div>
                    <div className="p-2.5 bg-teal-50 rounded-lg">
                      <Building2 className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Active Trials</p>
                      <p className="text-2xl font-bold text-amber-600 mt-1">{kpis?.active_trials || 0}</p>
                    </div>
                    <div className="p-2.5 bg-amber-50 rounded-lg">
                      <Activity className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Consultants</p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">{kpis?.total_consultants || 0}</p>
                    </div>
                    <div className="p-2.5 bg-blue-50 rounded-lg">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Total Users</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{kpis?.total_users || 0}</p>
                    </div>
                    <div className="p-2.5 bg-slate-100 rounded-lg">
                      <Users className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Breakdown */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Revenue Sources
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Subscription Revenue</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(kpis?.subscription_revenue || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {kpis?.subscription_breakdown?.small_business || 0} SB + {kpis?.subscription_breakdown?.growth || 0} Growth + {kpis?.subscription_breakdown?.enterprise || 0} Enterprise
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Pursuit Slot Sales</p>
                    <p className="text-2xl font-bold text-teal-600">
                      {formatCurrency(kpis?.slot_revenue || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {kpis?.total_slots_sold || 0} slots @ $150/slot
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Avg Revenue Per Contractor</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(kpis?.arpc || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Lifetime value indicator
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Note about data freshness */}
            <p className="text-center text-sm text-slate-400">
              KPIs refresh automatically. Last updated: {new Date().toLocaleTimeString()}
            </p>

            {/* Danger Zone — temporary Super-Admin destructive operations */}
            <Card className="border-red-200 bg-red-50/40" data-testid="danger-zone-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-red-800 text-lg">Danger Zone</CardTitle>
                <p className="text-xs text-red-700/80">
                  Temporary data-maintenance controls. Each action is irreversible,
                  Super-Admin-only, and recorded in the audit log. Remove once no
                  longer needed.
                </p>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <DangerZoneButton
                  label="Clear All Pursuits & Pipeline Data"
                  testId="clear-pursuits-pipeline-btn"
                  endpoint="/api/admin/danger/clear-pursuits-and-pipeline"
                  confirmTitle="Clear all pursuits and pipeline data?"
                  confirmDescription={
                    <div>
                      Deletes <strong>every pursuit across every tenant</strong>, plus all
                      descendant collections (scorecards, matrices, requirements, extraction
                      jobs, drafts, evaluations, sentinel opportunities, leads, trial
                      requests, tenant settings, consultant profiles &amp; links, and more).
                      <br /><br />
                      Users, organizations, billing, audit logs, and policy acceptances are
                      preserved.
                    </div>
                  }
                  onSuccess={fetchKPIs}
                />
                <DangerZoneButton
                  label="Clear Test Users & Orgs"
                  testId="clear-test-users-orgs-btn"
                  endpoint="/api/admin/danger/clear-test-users-and-orgs"
                  confirmTitle="Clear test users and organizations?"
                  confirmDescription={
                    <div>
                      Deletes every user <strong>except</strong> the two Super Admins
                      (<code className="text-xs">thepkg@gmail.com</code>,{' '}
                      <code className="text-xs">pk@fedbd.ai</code>) and the four functional
                      test accounts (<code className="text-xs">pgundepudi</code>,{' '}
                      <code className="text-xs">mkg019</code>,{' '}
                      <code className="text-xs">pavankg.oe</code>,{' '}
                      <code className="text-xs">reviewer@mkggroup.com</code>).
                      <br /><br />
                      Deletes every organization <strong>except</strong> MNGC Group, Inc.,
                      MKG Federal Consulting, and MKG Group LLC.
                    </div>
                  }
                  onSuccess={fetchKPIs}
                />
              </CardContent>
            </Card>

            {/* Sentinel Data Management — scoped cleanup by org */}
            <Card className="border-orange-200 bg-orange-50/30" data-testid="sentinel-cleanup-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-orange-800 text-lg">Sentinel Data Management</CardTitle>
                <p className="text-xs text-orange-700/80">
                  Clear SAM or eBuy sentinel data for a specific client org. Use for
                  end-of-trial cleanup or dev resets. Actions are Super-Admin-only and
                  recorded in the audit log.
                </p>
              </CardHeader>
              <CardContent>
                <SentinelCleanupPanel />
              </CardContent>
            </Card>

            {/* Database Migration — server-to-server copy to a new cluster */}
            <Card className="border-indigo-200 bg-indigo-50/30" data-testid="db-migration-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-indigo-800 text-lg">Database Migration</CardTitle>
                <p className="text-xs text-indigo-700/80">
                  Copy the live database to a new MongoDB cluster (e.g. moving off a managed host).
                  Server-to-server, no local tooling. The target connection is stored encrypted;
                  the run wipes &amp; overwrites the target. Super-Admin only, audited.
                </p>
              </CardHeader>
              <CardContent>
                <DbMigrationPanel />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
